import * as React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";
import { useHistory } from "react-router-dom";

import { Button } from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import {
  ListPendingCustomerPagination,
  usePendingCustomerApi,
  PendingCustomerStatus,
  IPendingCustomer
} from "src/core/pending-customers/api/pendingCustomer.api";
import { Text } from "src/library/components/Text";
import {
  CheckboxStyled,
  Select
} from "src/library/components/Forms";
import { PendingCustomersExportModal } from "src/core/pending-customers/components/PendingCustomersExportModal";
import { Table } from "src/core/dashboard/components/widgets";
import { pendingCustomersTableColumns } from "src/core/pending-customers/components/tables/pendingCustomerTableColumns";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { FilterParams } from "src/library/utils";
import {
  IUser,
  IUserGroup,
  useUserApi
} from "src/core/users/api/users.api";
import { useLocalStorage } from "src/library/hooks";
import { useCurrentUser } from "src/core/users/hooks";
import SelectedUser from "src/core/users/components/SelectedUser";
import SelectedUserGroup from "src/core/users/components/SelectedUserGroup";

const defaultPagination: ListPendingCustomerPagination = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "name", "ASC" ] ]
};

const localStorageKeys = {
  SELECTED_FILTER: "pending-customers_selected-filter",
  QUICK_FILTER: "pending-customers_quick-filter"
};

type QuickFilter = "currentUser" | "assignedUser" | "userGroup" | "all";

interface QuickFilters extends Partial<Pick<IPendingCustomer, "assignedUserId" | "userGroupId">> {
  currentUserId?: number;
  all?: null;
}

const quickFilterOptions: Array<{text: string; value: QuickFilter }> = [
  {
    text: "Assigned To Me",
    value: "currentUser"
  },
  {
    text: "Assigned User",
    value: "assignedUser"
  },
  {
    text: "User Group",
    value: "userGroup"
  },
  {
    text: "All",
    value: "all"
  }
];

/**
 * Main "Pending Customers" View
 *
 * @returns
 */
const PendingCustomersPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const [ showExportModal, setShowExportModal ] = React.useState(false);
  const [ pendingCustomersResponse, getPendingCustomers ] = usePendingCustomerApi("LIST_PENDING_CUSTOMERS");
  const [ getUserResponse, getUser ] = useUserApi("GET_USER");
  const [ getUserGroupResponse, getUserGroup ] = useUserApi("GET_USER_GROUP");
  const tableData = React.useMemo(() => pendingCustomersResponse.data?.result || [], [ pendingCustomersResponse.data ]);
  const [ includeProcessedPendingCustomers, setIncludeProcessedPendingCustomers ] = React.useState(false);
  const terminology = useTerminology();
  const [ quickFilters, setQuickFilters ] = useLocalStorage(localStorageKeys.QUICK_FILTER, "{}");
  const [ assignedUser, setAssignedUser ] = React.useState<IUser | null>(null);
  const [ selectedUserGroup, setSelectedUserGroup ] = React.useState<IUserGroup | null>(null);
  const [ { currentUser } ] = useCurrentUser();
  const [ selectedQuickFilter, setSelectedQuickFilter ] = useLocalStorage<QuickFilter>(localStorageKeys.SELECTED_FILTER, "currentUser");
  const history = useHistory();

  // quick filter persisted in local storage
  const quickFilter: QuickFilters = React.useMemo(() => {
    try {
      return JSON.parse(quickFilters || "{}");
    } catch (error) {
      return {};
    }
  }, [ quickFilters ]);

  // queries pending customers
  const onFetchData = React.useCallback((params: {pagination: ListPendingCustomerPagination, filters: FilterParams<IPendingCustomer>}) => {
    if (Object.keys(quickFilter).length === 0 && selectedQuickFilter === "currentUser" && currentUser) {
      // we've mounted and setting local storage for first time
      // set relevant values and filter
      setAssignedUser(currentUser);
      setQuickFilters(JSON.stringify({ assignedUserId: currentUser.id }));
      params.filters.assignedUserId = currentUser.id;
    }

    // sanatise quick filter "all" option
    if (Object.keys(quickFilter).includes("all")) {
      delete quickFilter.all;
    }

    // cast null quick filter values into undefined to satisfy route filter validations
    const sanitisedQuickFilters = Object.entries(quickFilter).reduce((acc, [ key, value ],) => {
      return {
        ...acc,
        [ key ]: value ?? undefined
      };
    }, {});

    getPendingCustomers({
      pagination: params.pagination,
      filters: {
        status: includeProcessedPendingCustomers ? undefined : PendingCustomerStatus.Pending,
        ...params.filters,
        ...sanitisedQuickFilters
      }
    });
  }, [
    currentUser,
    getPendingCustomers,
    includeProcessedPendingCustomers,
    quickFilter,
    selectedQuickFilter,
    setQuickFilters
  ]);

  // used to manage local state for quick filters and update table data
  const applyQuickFilter = React.useCallback((type: QuickFilter, data?: IUser | IUserGroup | null) => {
    let filterKey: keyof QuickFilters = "all";

    // update local state for displaying selected value
    if (type === "assignedUser" || type === "currentUser") {
      if (type === "assignedUser") {
        setAssignedUser(data as IUser);
      }

      filterKey = "assignedUserId";
    } else if (type === "userGroup") {
      setSelectedUserGroup(data as IUserGroup);
      filterKey = "userGroupId";
    } else if (type === "all") {
      // reset data and return
      setAssignedUser(null);
      setSelectedUserGroup(null);
    }

    // set filter value based on type
    const filterValue = type === "currentUser" ? currentUser?.id : data?.id || null;

    try {
      // update quick filters for table filters to use
      setQuickFilters(JSON.stringify({ [ filterKey ]: filterValue }));
    } catch (error) {
      // soft fail if local storage fails
    }

    if (type === "all") {
      // ensure tableFilters don't include assignedUser or userGroup filters
      // and refetch table data with no quick filter applied
      getPendingCustomers({
        filters: { },
        pagination: defaultPagination
      });
    } else {
      // refetch table data with relevant filter - inc tableFilters
      getPendingCustomers({
        filters: { [ filterKey ]: filterValue ?? undefined },
        pagination: defaultPagination
      });
    }
  }, [
    currentUser?.id,
    getPendingCustomers,
    setQuickFilters
  ]);

  // If quick filter updates, dispatch call to fetch the user or group.
  React.useEffect(() => {
    if (quickFilter?.assignedUserId) {
      getUser({ id: quickFilter.assignedUserId });
    }

    if (quickFilter?.userGroupId) {
      getUserGroup({ id: quickFilter.userGroupId });
    }
  }, [
    quickFilter,
    getUser,
    getUserGroup
  ]);

  // update assignedUser if request returns user
  React.useEffect(() => {
    if (getUserResponse.data) {
      setAssignedUser(getUserResponse.data);
    }
  }, [ getUserResponse ]);

  // update selectedUserGroup if request returns user
  React.useEffect(() => {
    if (getUserGroupResponse.data) {
      setSelectedUserGroup(getUserGroupResponse.data);
    }
  }, [ getUserGroupResponse ]);

  const LikeToDoActions: React.ReactElement[] = React.useMemo(() => {
    const actions: React.ReactElement[] = [
      <Button
        key="export-pending-customers"
        onClick={() => setShowExportModal(true)}
      >
        Export Pending Customers
      </Button>
    ];

    return actions;
  }, []);

  const tableColumns = React.useMemo(() => {
    return pendingCustomersTableColumns(info => {
      return (
        <Button
          $buttonType="secondary"
          onClick={() => history.push(`/admin/pending-customers/${info.row.original.id}`)}
        >
          Edit
        </Button>
      );
    });
  }, [ history ]);

  return (
    <Layout {...props}>
      <MainContentWrapper
        fill
        Header={(
          <LikeToDo
            copy="What would you like to do?"
            buttons={LikeToDoActions}
          />
        )}
      >
        <AdditionalFiltersStyled className="additional-filters">
          <div className="quick-filter">
            <div className="header">
              <Text weight="bold">
                Quick Filters
              </Text>
            </div>

            <div className="top-filters">
              <Select
                className="quick-filter-select"
                as="select"
                name="quick-filter"
                label="quick filter"
                autoHideLabels
                handleChange={e => {
                  setSelectedQuickFilter(e.target.value);
                  applyQuickFilter(e.target.value);
                }}
                options={quickFilterOptions}
                value={selectedQuickFilter}
              />

              <div className="include-processed">
                <CheckboxStyled
                  checked={includeProcessedPendingCustomers}
                  onChange={e => setIncludeProcessedPendingCustomers(e.target.checked)}
                />

                <Text
                  className="include-label"
                  weight="bold"
                >
                  Include processed customers:
                </Text>
              </div>
            </div>

            {selectedQuickFilter === "assignedUser" && (
              <div className="sub-filter">
                <Text>
                  Selected User:
                </Text>

                <SelectedUser
                  user={assignedUser}
                  handleUpdate={data => applyQuickFilter("assignedUser", data)}
                  terminology={terminology}
                />
              </div>
            )}

            {selectedQuickFilter === "userGroup" && (
              <div className="sub-filter user-group">
                <Text>
                  Selected Group:
                </Text>

                <SelectedUserGroup
                  userGroup={selectedUserGroup}
                  handleUpdate={data => applyQuickFilter("userGroup", data)}
                />
              </div>
            )}
          </div>
        </AdditionalFiltersStyled>

        <Table
          tableOf={`Pending ${terminology?.customer.plural.text}`}
          className="large"
          data={tableData}
          columns={tableColumns}
          onFetchData={onFetchData}
          defaultPagination={defaultPagination}
          loading={pendingCustomersResponse.pending}
          pageCount={pendingCustomersResponse.data?.pages}
          rowCount={pendingCustomersResponse.data ? pendingCustomersResponse.data.count : 0}
          pageSizeOptions={[
            10,
            20,
            50,
            100
          ]}
        />
      </MainContentWrapper>

      <PendingCustomersExportModal
        open={showExportModal}
        onDismiss={() => setShowExportModal(false)}
      />

    </Layout>
  );
};

const AdditionalFiltersStyled = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing.margins.sm};
    .quick-filter {
      .header {
        margin-bottom: ${theme.spacing.margins.md};
      }

      .top-filters {
        display: flex;
        align-items: center;

        .include-processed {
          margin-left: ${theme.spacing.margins.xl};
          display: flex;
          align-items: center;

          .include-label {
            margin-left: ${theme.spacing.margins.md}
          }
        }
      }

      .sub-filter {
        margin-top: ${theme.spacing.margins.sm};
        display: flex;
        align-items: center;
        span {
          margin-right: ${theme.spacing.margins.md};
        }
      }

      .selected-user {
        min-width: 300px;
        border: solid 1px ${theme.colors.lightGrey};
        padding: 0 ${theme.spacing.padding.md};
        height: 36px;
      }

      .user-search-component {
        border: solid 1px ${theme.colors.lightGrey};
        max-width: 300px;
      }

      .user-group {
        select {
          width: 293px;
        }
      } 
    }
  `}  
`;

export default PendingCustomersPage;
